import React from 'react';
import { Box, IconButton, Typography } from '@ublend-npm/aulaui-core';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { OutlookThreadMessage } from '../types';

type AttachmentItem = NonNullable<OutlookThreadMessage['attachments']>[number];

type OutlookAttachmentProps = {
  attachment: AttachmentItem;
};

/**
 * Renders a single attachment row with file icon, name/size, and download button.
 */
const OutlookAttachment = ({ attachment }: OutlookAttachmentProps) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
    height="60px"
    p="8px 16px"
    sx={{
      border: '1px solid',
      borderColor: (theme) =>
        theme.palette.mode === 'dark' ? '#3A3247' : 'rgba(35, 31, 41, 0.16)',
      borderRadius: '4px',
      bgcolor: (theme) =>
        theme.palette.mode === 'dark' ? '#201A29' : '#F8F7F9',
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={40}
      height={40}
      flexShrink={0}
      sx={{
        border: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? '#413A50' : '#E2E8F0',
        borderRadius: '8px',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? '#1B1622' : '#FFFFFF',
      }}
    >
      <PictureAsPdfOutlinedIcon sx={{ color: '#F44343', fontSize: 24 }} />
    </Box>

    <Box minWidth={0} flex={1}>
      <Typography
        variant="subtitle2"
        fontWeight={500}
        noWrap
        sx={{
          fontSize: '14px',
          lineHeight: '21px',
          letterSpacing: '0.1px',
          color: (theme) =>
            theme.palette.mode === 'dark' ? '#FFFFFF' : '#0F172A',
        }}
      >
        {attachment.name}
      </Typography>
      {attachment.size ? (
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          {attachment.size}
        </Typography>
      ) : null}
    </Box>

    <IconButton
      aria-label={`Download ${attachment.name}`}
      sx={{
        width: 40,
        height: 40,
        bgcolor: '#B0BEC5',
        color: 'common.white',
        '&:hover': { bgcolor: '#90A4AE' },
      }}
    >
      <FileDownloadIcon />
    </IconButton>
  </Box>
);

export default OutlookAttachment;
